import { useEffect, useCallback, useRef } from 'react';

import { trackEvent } from '../utils/analytics';

/**
 * Hook to detect machine translation (Google Translate, etc.) based on DOM changes
 * Uses the method described in: https://martijnhols.nl/blog/how-to-detect-google-translate-and-other-machine-translation
 */
export const useTrackTranslation = () => {
  const originalLanguageRef = useRef<string>('');
  const observersRef = useRef<MutationObserver[]>([]);

  const handleTranslationDetected = useCallback((originalLang: string, newLang: string, method: 'generic' | 'edge') => {
    console.info(
      `Page translation detected (${method === 'generic' ? 'Generic' : 'Microsoft Edge'})`,
      `lang="${newLang}" (original="${originalLang}")`
    );

    // Track analytics event
    void trackEvent('TranslationDetected', {
      eventMetadata: {
        originalLanguage: originalLang,
        newLanguage: newLang,
        detectionMethod: method,
      },
    });
  }, []);

  useEffect(() => {
    // Store the original language
    originalLanguageRef.current = document.documentElement.lang || 'en';

    // Generic detector for Chrome, Firefox, Safari and most browsers
    const langObserver = new MutationObserver(() => {
      const currentLang = document.documentElement.lang;
      const originalLang = originalLanguageRef.current;

      if (currentLang !== originalLang) {
        // Translation was applied
        handleTranslationDetected(originalLang, currentLang, 'generic');
      }
    });

    langObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['lang'],
      childList: false,
      characterData: false,
    });

    observersRef.current.push(langObserver);

    // Microsoft Edge specific detector
    const titleElement = document.getElementsByTagName('title')[0];

    if (titleElement) {
      const edgeObserver = new MutationObserver(() => {
        if (titleElement.hasAttribute('_msttexthash')) {
          handleTranslationDetected(originalLanguageRef.current, 'unknown', 'edge');
        }
      });

      edgeObserver.observe(titleElement, {
        attributes: true,
        attributeFilter: ['_msttexthash'],
        childList: false,
        characterData: false,
      });

      observersRef.current.push(edgeObserver);
    }

    // Cleanup function
    return () => {
      observersRef.current.forEach((observer) => observer.disconnect());
      observersRef.current = [];
    };
  }, [handleTranslationDetected]);
};
